import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { okSchema, revealSchema, type VendorKey, type VendorSettings, vendorSettingsSchema } from './vendors'

const tenantSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  banner: z.string().nullable(),
  merakiEnabled: z.boolean().nullable(),
  cfEnabled: z.boolean().nullable(),
  extremeEnabled: z.boolean().nullable(),
  intuneEnabled: z.boolean().nullable(),
  cradlepointEnabled: z.boolean().nullable(),
})
export type TenantSummary = z.infer<typeof tenantSummarySchema>

const tenantsEnvelopeSchema = z.object({ success: z.boolean(), data: z.array(tenantSummarySchema) })
const tenantDetailEnvelopeSchema = z.object({ success: z.boolean(), data: vendorSettingsSchema })
const createdSchema = z.object({
  success: z.boolean(),
  data: z.object({ orgId: z.string(), name: z.string(), slug: z.string() }),
})
export type CreatedTenant = z.infer<typeof createdSchema>['data']

export const adminTenantsQueryKey = ['admin', 'tenants'] as const
export const adminTenantQueryKey = (orgId: string) => ['admin', 'tenants', orgId] as const

/** Pure: integration badges for a tenant row, in display order. */
export function tenantIntegrations(t: TenantSummary): { vendor: VendorKey; label: string; enabled: boolean }[] {
  return [
    { vendor: 'meraki', label: 'Meraki', enabled: t.merakiEnabled === true },
    { vendor: 'cloudflare', label: 'CF', enabled: t.cfEnabled === true },
    { vendor: 'extreme', label: 'Extreme', enabled: t.extremeEnabled === true },
    { vendor: 'intune', label: 'Intune', enabled: t.intuneEnabled === true },
    { vendor: 'cradlepoint', label: 'CP', enabled: t.cradlepointEnabled === true },
  ]
}

/** Pure: URL-safe tenant slug suggestion from a display name. */
export const slugifyTenant = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/** All tenants (netops only). */
export function useAdminTenants() {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-tenants'] : adminTenantsQueryKey,
    queryFn: async (): Promise<TenantSummary[]> => {
      const res = await api.get('/v1/admin/tenants', tenantsEnvelopeSchema)
      if (!res.success) throw new Error('Failed to fetch tenants')
      return res.data
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.adminTenants, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/** One tenant's settings with masked credentials (same shape as credentials: `vendorBlock(data, vendor)`). */
export function useAdminTenant(orgId: string) {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-tenant'] : adminTenantQueryKey(orgId),
    queryFn: async (): Promise<VendorSettings> => {
      const res = await api.get(`/v1/admin/tenants/${encodeURIComponent(orgId)}`, tenantDetailEnvelopeSchema)
      if (!res.success) throw new Error('Failed to fetch tenant')
      return res.data
    },
    enabled: !preview && orgId.length > 0,
    ...(preview ? { initialData: preview.adminTenantDetail, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/** `mutateAsync({ name, slug, banner? })` -> `{ orgId, name, slug }` (POST /v1/admin/tenants). 400 on duplicate slug. */
export function useCreateTenant() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { name: string; slug: string; banner?: string }): Promise<CreatedTenant> => {
      if (preview) return { orgId: 'org_preview', name: v.name, slug: v.slug }
      return (await api.request('/v1/admin/tenants', { method: 'POST', body: v }, createdSchema)).data
    },
    onSuccess: async () => {
      if (!preview) await qc.invalidateQueries({ queryKey: adminTenantsQueryKey })
    },
  })
}

/** `mutate({ orgId })` DELETE /v1/admin/tenants/:orgId (destructive; confirm first). */
export function useDeleteTenant() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { orgId: string }) => {
      if (preview) return { success: true }
      return api.request(`/v1/admin/tenants/${encodeURIComponent(v.orgId)}`, { method: 'DELETE' }, okSchema)
    },
    onSuccess: async () => {
      if (!preview) await qc.invalidateQueries({ queryKey: adminTenantsQueryKey })
    },
  })
}

/** `mutate({ orgId, vendor, data })` PATCH /v1/admin/tenants/:orgId; build `data` with `vendorPatchBody`/`vendorToggleBody`. */
export function useUpdateTenantCredentials() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async ({ orgId, ...body }: { orgId: string; vendor: VendorKey; data: Record<string, unknown> }) => {
      if (preview) return { success: true }
      return api.request(`/v1/admin/tenants/${encodeURIComponent(orgId)}`, { method: 'PATCH', body }, okSchema)
    },
    onSuccess: async (_r, v) => {
      if (preview) return
      await Promise.all([
        qc.invalidateQueries({ queryKey: adminTenantQueryKey(v.orgId) }),
        qc.invalidateQueries({ queryKey: adminTenantsQueryKey, exact: true }),
      ])
    },
  })
}

/** Unmask a tenant secret. `mutateAsync({ orgId, vendor, field })` -> `string` (POST .../reveal; read-only, never cached). */
export function useRevealTenantCredential() {
  const api = useApi()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async ({ orgId, ...body }: { orgId: string; vendor: VendorKey; field: string }): Promise<string> => {
      if (preview) return 'preview-secret-value'
      const res = await api.request(
        `/v1/admin/tenants/${encodeURIComponent(orgId)}/reveal`,
        { method: 'POST', body },
        revealSchema,
      )
      return res.data.value
    },
    gcTime: 0,
  })
}
